import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Button,
  Card,
  Progress,
  CircularProgress,
} from '../../components';
import { useInspectionContext } from '../../hooks';
import { REQUIRED_ANGLES, type Angle } from '../../types/api';
import { getAngleLabel } from '../../api';
import { formatFileSize } from '../../utils';

export const UploadPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    state,
    uploadInspection,
    startPolling,
    reset,
  } = useInspectionContext();

  const [uploadStage, setUploadStage] = useState<'preparing' | 'uploading' | 'processing' | 'done' | 'error'>('preparing');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const doUpload = async () => {
      setUploadStage('uploading');
      setErrorMessage(null);

      const result = await uploadInspection();
      if (cancelled) return;

      if (result.success && result.inspectionId) {
        setUploadStage('processing');
        startPolling(result.inspectionId);
        
        // Navigate to status page after a brief moment
        setTimeout(() => {
          navigate('/inspecao/status', { replace: true });
        }, 1500);
      } else {
        setUploadStage('error');
        setErrorMessage(result.error || 'Erro desconhecido no upload');
      }
    };

    doUpload();

    return () => {
      cancelled = true;
    };
  }, [uploadInspection, startPolling, navigate, retryCount]);

  const handleRetry = () => {
    setUploadStage('preparing');
    setErrorMessage(null);
    // Re-tenta SEM reload — o provider mantém fotos/vídeo capturados;
    // window.location.reload() descartaria todo o estado do fluxo.
    setRetryCount((c) => c + 1);
  };

  const handleCancel = () => {
    reset();
    navigate('/inspecao/placa', { replace: true });
  };

  const totalSize =
    Object.values(state.photos).reduce((sum, p) => sum + (p?.file.size ?? 0), 0) +
    (state.video?.file.size ?? 0);

  return (
    <Page
      appBar={{
        title: 'Enviando Vistoria',
        subtitle: state.plate,
        elevation: false,
        transparent: true,
      }}
      className="pb-24"
    >
      <div className="flex flex-col items-center justify-center flex-1 px-4 text-center">
        {/* Animation / Icon */}
        <div className="mb-8 relative">
          {uploadStage === 'preparing' && (
            <CircularProgress value={0} size={120} strokeWidth={6} className="text-primary" />
          )}
          {uploadStage === 'uploading' && (
            <CircularProgress value={state.uploadProgress} size={120} strokeWidth={6} className="text-primary" />
          )}
          {uploadStage === 'processing' && (
            <div className="w-32 h-32 rounded-full bg-primary/10 flex items-center justify-center mx-auto animate-pulse-soft">
              <span className="material-symbols-outlined text-primary text-5xl">cloud_queue</span>
            </div>
          )}
          {uploadStage === 'done' && (
            <div className="w-32 h-32 rounded-full bg-success-bg flex items-center justify-center mx-auto">
              <span className="material-symbols-outlined text-success text-5xl">check_circle</span>
            </div>
          )}
          {uploadStage === 'error' && (
            <div className="w-32 h-32 rounded-full bg-error-container flex items-center justify-center mx-auto">
              <span className="material-symbols-outlined text-error text-5xl">error</span>
            </div>
          )}
        </div>

        {/* Status Text */}
        <h2 className="font-headline-lg mb-2">
          {uploadStage === 'preparing' && 'Preparando envio...'}
          {uploadStage === 'uploading' && `Enviando ${Math.round(state.uploadProgress)}%`}
          {uploadStage === 'processing' && 'Processando na nuvem...'}
          {uploadStage === 'done' && 'Vistoria enviada!'}
          {uploadStage === 'error' && 'Erro no envio'}
        </h2>

        <p className="font-body-md text-on-surface-variant mb-6 max-w-sm">
          {uploadStage === 'preparing' && 'Comprimindo e preparando as fotos...'}
          {uploadStage === 'uploading' && `Enviando ${REQUIRED_ANGLES.length} fotos${state.video ? ' + vídeo' : ''} (${formatFileSize(totalSize)})`}
          {uploadStage === 'processing' && 'Nossa IA está analisando as imagens. Isso pode levar alguns instantes.'}
          {uploadStage === 'done' && 'Redirecionando para acompanhamento...'}
          {uploadStage === 'error' && errorMessage}
        </p>

        {/* Progress Bar */}
        {(uploadStage === 'preparing' || uploadStage === 'uploading') && (
          <Progress
            value={uploadStage === 'preparing' ? 10 : state.uploadProgress}
            max={100}
            size="lg"
            showLabel
            className="w-full max-w-md mb-6"
          />
        )}

        {/* Photo Summary */}
        <Card variant="elevated" padding="md" className="w-full max-w-md mb-6">
          <h3 className="font-label-lg mb-3 text-center">Fotos a enviar</h3>
          <div className="grid grid-cols-4 gap-2">
            {REQUIRED_ANGLES.map((angle: Angle) => {
              const photo = state.photos[angle];
              return (
                <div key={angle} className="text-center">
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-surface-container mx-auto mb-1 relative">
                    {photo ? (
                      <img src={photo.preview} alt={getAngleLabel(angle)} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-on-surface-variant">
                        <span className="material-symbols-outlined">photo_camera</span>
                      </div>
                    )}
                    {photo && (
                      <div className="absolute bottom-1 right-1 w-5 h-5 bg-success rounded-full flex items-center justify-center">
                        <span className="material-symbols-outlined text-[10px] text-on-success">check</span>
                      </div>
                    )}
                  </div>
                  <span className="font-label-sm text-center block truncate">{getAngleLabel(angle)}</span>
                </div>
              );
            })}
          </div>
          <div className="mt-3 pt-3 border-t border-outline-variant flex justify-between text-body-sm">
            <span>Total de fotos</span>
            <span className="font-label-md">{Object.keys(state.photos).length}/{REQUIRED_ANGLES.length}</span>
          </div>
          <div className="flex justify-between text-body-sm">
            <span>Vídeo do motor</span>
            <span className="font-label-md">
              {state.video ? formatFileSize(state.video.file.size) : 'Pulado'}
            </span>
          </div>
          <div className="flex justify-between text-body-sm">
            <span>Tamanho total</span>
            <span className="font-label-md">{formatFileSize(totalSize)}</span>
          </div>
        </Card>

        {/* Actions */}
        <div className="flex flex-col gap-3 w-full max-w-md">
          {uploadStage === 'error' && (
            <>
              <Button variant="primary" fullWidth onClick={handleRetry} leftIcon={<span className="material-symbols-outlined">refresh</span>}>
                Tentar Novamente
              </Button>
              <Button variant="outline" fullWidth onClick={handleCancel}>
                Cancelar
              </Button>
            </>
          )}
          {uploadStage === 'done' && (
            <Button variant="primary" fullWidth onClick={() => navigate('/inspecao/status')} leftIcon={<span className="material-symbols-outlined">visibility</span>}>
              Acompanhar Status
            </Button>
          )}
        </div>
      </div>
    </Page>
  );
};